import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Controller } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router-dom"

import { ThreeDotsLoader } from "@atoms"
import laparoliereLogo from "assets/laparoliere-logo.png"
import useLanguage from "components/user-settings/use-change-language"
import shell from "../email-registration/styles.module.scss"
import { useCreateMember, useVerifyTokenValidity } from "./queries"
import styles from "./styles.module.scss"
import { useRegistrationForm } from "./use-form"

const Registration = (): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")

  useEffect(() => {
    if (!token) navigate("../email-registration")
  }, [navigate, token])

  const tokenValidity = useVerifyTokenValidity(
    { token: token ?? "" },
    { enabled: !!token }
  )

  const createMember = useCreateMember()
  const {
    handleSubmit,
    setError,
    control,
    watch,
    formState: { errors },
  } = useRegistrationForm({
    defaultValues: {
      token: token ?? "",
      type: "regular",
    },
  })

  const onSubmit = handleSubmit((data) => {
    createMember.mutate(data as any, {
      onError(error: any) {
        if (error.response.data?.code === "form/validation") {
          for (const key in error.response.data.errors) {
            setError(key as any, {
              message: error.response.data.errors[key][0].message,
            })
          }
        }
      },
    })
  })

  if (!token || tokenValidity.isLoading) {
    return (
      <Shell>
        <IsVerifying />
      </Shell>
    )
  }

  if (
    tokenValidity.isError &&
    tokenValidity.error?.response?.data.code === "member/email-exist"
  ) {
    return (
      <Shell>
        <ErrorState error="email_exist_error" />
      </Shell>
    )
  }

  if (tokenValidity.isError) {
    return (
      <Shell>
        <ErrorState />
      </Shell>
    )
  }

  if (createMember.isSuccess) {
    return (
      <Shell>
        <Success
          hasPassword={!!watch("password")}
          isMember={createMember.variables?.type === "regular"}
        />
      </Shell>
    )
  }

  return (
    <Shell>
      <div className={shell.intro}>
        <p className={shell.lead}>
          {t("external.members.registration_form_intro", {
            lettersCase: "none",
          })}
        </p>
      </div>
      <form onSubmit={onSubmit} noValidate>
        <div className={styles.grid}>
          <Field
            id="first_name"
            label={t("global.first_name")}
            required
            error={errors.first_name?.message}
          >
            <Controller
              name="first_name"
              control={control}
              render={({ field }) => (
                <input
                  id="first_name"
                  autoComplete="given-name"
                  placeholder={t("global.first_name")}
                  className={inputClass(!!errors.first_name)}
                  {...field}
                />
              )}
            />
          </Field>
          <Field
            id="street_1"
            label={t("global.street_1")}
            error={errors.address?.street_1?.message}
          >
            <Controller
              name="address.street_1"
              control={control}
              render={({ field }) => (
                <input
                  id="street_1"
                  autoComplete="address-line1"
                  placeholder={t("global.street_1")}
                  className={inputClass(!!errors.address?.street_1)}
                  {...field}
                  value={field.value ?? ""}
                />
              )}
            />
          </Field>
          <Field
            id="last_name"
            label={t("global.last_name")}
            required
            error={errors.last_name?.message}
          >
            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <input
                  id="last_name"
                  autoComplete="family-name"
                  placeholder={t("global.last_name")}
                  className={inputClass(!!errors.last_name)}
                  {...field}
                />
              )}
            />
          </Field>
          <Field
            id="street_2"
            label={t("global.street_2")}
            error={errors.address?.street_2?.message}
          >
            <Controller
              name="address.street_2"
              control={control}
              render={({ field }) => (
                <input
                  id="street_2"
                  autoComplete="address-line2"
                  placeholder={t("global.street_2")}
                  className={inputClass(!!errors.address?.street_2)}
                  {...field}
                  value={field.value ?? ""}
                />
              )}
            />
          </Field>
          <Field
            id="birthdate"
            label={t("global.birthdate")}
            error={errors.birthdate?.message}
          >
            <Controller
              name="birthdate"
              control={control}
              render={({ field }) => (
                <input
                  id="birthdate"
                  type="date"
                  autoComplete="bday"
                  className={inputClass(!!errors.birthdate)}
                  {...field}
                  value={field.value ?? ""}
                />
              )}
            />
          </Field>
          <Field
            id="city"
            label={t("global.city")}
            error={errors.address?.city?.message}
          >
            <Controller
              name="address.city"
              control={control}
              render={({ field }) => (
                <input
                  id="city"
                  autoComplete="address-level2"
                  placeholder={t("global.city")}
                  className={inputClass(!!errors.address?.city)}
                  {...field}
                  value={field.value ?? ""}
                />
              )}
            />
          </Field>
          <Field
            id="phone"
            label={t("global.phone")}
            error={errors.phone?.message}
          >
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder={t("global.phone")}
                  className={inputClass(!!errors.phone)}
                  {...field}
                  value={field.value ?? ""}
                  onChange={(event) => {
                    const value = event.target.value
                    if (
                      value.match(
                        /^(?:(?:\()?([0-9]{1,3})(?:\)?[-. ]?([0-9]{0,3})[-. ]?([0-9]{0,4}))?)?$/
                      ) ||
                      value === ""
                    ) {
                      field.onChange(value)
                    }
                  }}
                />
              )}
            />
          </Field>
          <Field
            id="zip"
            label={t("global.zip")}
            error={errors.address?.zip?.message}
          >
            <Controller
              name="address.zip"
              control={control}
              render={({ field }) => (
                <input
                  id="zip"
                  autoComplete="postal-code"
                  placeholder="A1A 1A1"
                  className={inputClass(!!errors.address?.zip)}
                  {...field}
                  value={field.value ?? ""}
                  onChange={(event) => {
                    const value = event.target.value.toUpperCase()
                    if (
                      value.match(
                        /^[ABCEGHJKLMNPRSTVXY]?[0-9]?[ABCEGHJKLMNPRSTVWXYZ]?[ -]?[0-9]?[ABCEGHJKLMNPRSTVWXYZ]?[0-9]?$/
                      ) ||
                      value === ""
                    ) {
                      field.onChange(value)
                    }
                  }}
                />
              )}
            />
          </Field>
        </div>

        <section className={styles.account}>
          <h2 className={shell.sectionTitle}>
            {t("external.members.register_to_activities_online")}
          </h2>
          <p className={styles.accountCopy}>
            {t("external.members.register_to_activities_online_msg", {
              lettersCase: "none",
            })}
          </p>
          <div className={styles.grid}>
            <Field
              id="password"
              label={t("auth.password")}
              required
              error={errors.password?.message}
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder={t("auth.password")}
                    className={inputClass(!!errors.password)}
                    {...field}
                  />
                )}
              />
            </Field>
            <Field
              id="password_confirmation"
              label={t("auth.password_confirmation")}
              required
              error={errors.password_confirmation?.message}
            >
              <Controller
                name="password_confirmation"
                control={control}
                render={({ field }) => (
                  <input
                    id="password_confirmation"
                    type="password"
                    autoComplete="new-password"
                    placeholder={t("auth.password")}
                    className={inputClass(!!errors.password_confirmation)}
                    {...field}
                  />
                )}
              />
            </Field>
          </div>
        </section>

        <button
          type="submit"
          className={shell.submit}
          disabled={createMember.isLoading}
        >
          {createMember.isLoading ? t("global.processing") : t("global.submit")}
        </button>
      </form>
    </Shell>
  )
}

function inputClass(invalid: boolean) {
  return invalid ? `${styles.input} ${styles.inputInvalid}` : styles.input
}

function Field({
  id,
  label,
  required,
  error,
  children,
}: {
  id: string
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
        {required ? <span className={styles.required}> *</span> : null}
      </label>
      {children}
      {error ? <span className={styles.fieldError}>{error}</span> : null}
    </div>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const { language, onChange } = useLanguage()
  const nextLanguage = language === "en" ? "fr" : "en"

  return (
    <div className={`${shell.page} ${styles.page}`}>
      <section className={`${shell.card} ${styles.card}`}>
        <div className={shell.brandRow}>
          <div className={shell.brand}>
            <img
              className={shell.logo}
              src={laparoliereLogo}
              alt="La Parolière, centre des femmes"
            />
            <p className={shell.orgName}>
              {t("external.members.org_name", { lettersCase: "none" })}
            </p>
          </div>
          <button
            type="button"
            className={shell.lang}
            onClick={() => onChange(nextLanguage)}
          >
            {nextLanguage === "en" ? "English" : "Français"}
          </button>
        </div>
        {children}
      </section>
    </div>
  )
}

function Success({
  hasPassword,
  isMember,
}: {
  hasPassword: boolean
  isMember: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <>
      <div className={shell.successMark} aria-hidden="true">
        <svg width="56" height="56" viewBox="0 0 56 56" focusable="false">
          <circle cx="28" cy="28" r="28" fill="#e8f6ee" />
          <circle cx="28" cy="28" r="20" fill="#1f9d55" />
          <path
            d="M18.5 28.5l6.2 6.2 12.8-13.4"
            fill="none"
            stroke="#fff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2 className={shell.successTitle}>
        {t("external.members.registration_completed")}
      </h2>
      <div className={shell.successCopy}>
        <p className={shell.copy}>
          {t("external.members.registration_completed_msg_1", {
            lettersCase: "none",
          })}
        </p>
        {!hasPassword && (
          <p className={shell.copy}>
            {t("external.members.registration_completed_msg_2", {
              lettersCase: "none",
            })}
          </p>
        )}
        {isMember && (
          <p className={shell.copy}>
            {t("external.members.registration_completed_msg_3", {
              lettersCase: "none",
            })}
          </p>
        )}
        <p className={shell.copy}>
          {t("external.members.registration_completed_msg_4", {
            lettersCase: "none",
          })}
        </p>
      </div>
      <button
        type="button"
        className={shell.submit}
        onClick={() => navigate("/")}
      >
        {t("global.my_portal")}
      </button>
    </>
  )
}

function IsVerifying() {
  const { t } = useTranslation()
  return (
    <>
      <h2 className={shell.sectionTitle}>{t("auth.verifying")}</h2>
      <div className={styles.loader}>
        <ThreeDotsLoader />
      </div>
    </>
  )
}

function ErrorState({ error = "link_error" }: { error?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <>
      <h2 className={shell.sectionTitle}>{t("errors.error")}</h2>
      <p className={shell.copy}>
        {t(`external.members.${error}`, {
          lettersCase: "none",
        })}
      </p>
      <button
        type="button"
        className={shell.submit}
        onClick={() => navigate("../email-registration")}
      >
        {t("global.go_back")}
      </button>
    </>
  )
}

export default Registration
